////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// drag/DragManager.ts — DragManager host class, full TS port (Phase 2.2).
// ----------------------------------------------------------------------------------------------------
// Moved verbatim from includes/js/template.js lines 20100–22202 in Phase 2.2.
// Singleton: bottom-of-file constructs `dragManager` and attaches both the
// class (`window.DragManager`) and the singleton (`window.dragManager`) so
// the pre-Phase-2 external API is preserved exactly.
//
// TokenDragController is imported directly instead of fetched off the
// FTEncounterMap.drag namespace. Static-method delegates that forwarded to
// FTEncounterMap.geom / FTEncounterMap.drag helpers now call the imports
// directly (shorter, same runtime behavior).
//
// Type strictness: loose per plan — most call sites use `any` for widget /
// element / encounter_arr; Phase 3 will tighten via shared/array-types.ts.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import { TokenDragController }         from './TokenDragController';
import { readInventoryEquippedToken } from '../../janus-engine/compile/inventory-raw-readers';
import {
    distance as geomDistance,
    clampGridCenterToEncounterVisibleBounds,
    footprintAabbFromCenterFeet,
    rectanglesOverlap,
    widestUncoveredXGap,
} from '../grid-math';
import {
    creatureBaseZFromDatasetSize,
    creatureIdSetFromEncounterArr,
    movementModeFromPreferredSpeedItemId,
} from '../drag-helpers';
import { encounterCreatureField } from '../../shared/array-types';
import type { EncounterArr, EncounterCreatureRow } from '../../shared/array-types';
import type { DragManagerHostLike, DragState } from './types';
import type { FT3DPoint, Interval, Rect } from '../grid-math';
import {
    tokenTeam,
    tokenIsDead,
    tokenIsMerchant,
    tokenReach,
    tokenGrossWeight,
    tokenUnitGrossWeight,
    tokenCapacityRemaining,
    tokenPushDragLiftCapacity,
    tokenPos,
} from '../token-data';


declare global {
    interface Window {
        DragManager?: typeof DragManager;
        dragManager?: DragManager;
        refreshDragAndDrop?: () => void;
        tradeState?: { active?: boolean; [key: string]: unknown };
        trainState?: { active?: boolean; [key: string]: unknown };
        ftGetActiveEncounterId?: () => string | null;
        ftGetActiveCasterId?: () => string | null;
        ftFindFullNestedItem?: (id: string) => unknown;
    }
}


// Module-private throttle — the singleton constructor below runs at bundle
// parse time (before template.js loads), so we can't depend on the
// `throttle()` global from template.js. Copy preserves the original
// trailing-edge-with-leading-call semantics from template.js line 19249.
type DragInputEvent = Event & {
    clientX?: number;
    clientY?: number;
    shiftKey?: boolean;
    touches?: TouchList;
    changedTouches?: TouchList;
};

interface LeafletHostElement extends Element {
    _ftLeafletMap?: {
        dragging?: { enabled?(): boolean; disable(): void; enable(): void };
        scrollWheelZoom?: { enabled?(): boolean; disable(): void; enable(): void };
    };
    _ftLeafletMapDragSuspended?: { dragging: boolean; scrollWheelZoom: boolean };
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object' && !Array.isArray(value)
        ? value as Record<string, unknown>
        : null;
}

function firstValue(value: unknown): unknown {
    return Array.isArray(value) ? value[0] : value;
}

function throttle<TArgs extends unknown[], TThis>(
    func: (this: TThis, ...args: TArgs) => void,
    limit: number,
): (this: TThis, ...args: TArgs) => void {
    let lastFunc: ReturnType<typeof setTimeout> | undefined;
    let lastRan: number | undefined;
    return function (this: TThis, ...args: TArgs) {
        if (!lastRan) {
            func.apply(this, args);
            lastRan = Date.now();
        } else {
            clearTimeout(lastFunc);
            lastFunc = setTimeout(() => {
                if ((Date.now() - (lastRan as number)) >= limit) {
                    func.apply(this, args);
                    lastRan = Date.now();
                }
            }, limit - (Date.now() - lastRan));
        }
    };
}


export class DragManager implements DragManagerHostLike {


    state: DragState;
    _tokenCtrl: TokenDragController;
    _bound: {
        handleMove: (e: DragInputEvent) => void;
        handleEnd: (e: DragInputEvent) => void;
        handleDragOver: (e: DragInputEvent) => void;
    };
    _lastLog?: unknown;
    _equipGhostKey = '';

    constructor() {
        this.state = {
            isDragging: false,
            draggedElement: null,
            draggedItemId: null,
            dropPreview: null,
            lastValidPosition: { clientX: 0, clientY: 0 },
            /** XZ map where the drag started (preview/line can move to other maps while dragging) */
            movementMode: 'ground',
            /** True when the active drag started from inventory owned by an elevated caster; flips the Shift contract in handleEnd. */
            _dragOverCleanup: null,

            // ==== NEW: mobile panel tab dwell (<xl viewports) ====
            tabDwell: null,               // { tab, which, timeoutId } while dwelling on a .ft-mobile-panel-tab

            // ==== NEW: encounter torch (#ft_mobile_panel_tab_enc) dwell while dragging inventory ====
            encTorchDwell: null,          // { tab, timeoutId } after arming scroll; tab reference blocks re-arm until pointer leaves
        };


        /**
         * Token drag (creature + inventory on the encounter map). Extracted in three
         * commits — Phase 1.1c-1 brings the small SVG/positioning helpers; 1.1c-2 adds
         * the setup methods; 1.1c-3 adds validation + the frame-by-frame update.
         */
        this._tokenCtrl = new TokenDragController(this);

        this._bound = {
            handleMove: this.handleMove.bind(this),
            handleEnd: this.handleEnd.bind(this),
            handleDragOver: throttle((e: DragInputEvent) => {
                e.preventDefault();
            }, 16),
        };
    }








    /**
     * Encounter creature ids in encounterArr (for possess_id chain walks).
     */
    static _creatureIdSetFromEncounterArr(encounterArr: EncounterArr | null | undefined): Set<string> {
        return creatureIdSetFromEncounterArr(encounterArr);
    }

    /**
     * From an inventory possess_id, walk parent inventory rows until we hit an encounter_creature id.
     */
    static _climbPossessToEncounterCreatureId(startPossessId: string, encounterArr: EncounterArr): string | null {
        if (!startPossessId || !encounterArr) return null;
        const creatureIds = DragManager._creatureIdSetFromEncounterArr(encounterArr);
        const inventories = encounterArr.encounter_inventories_arr || [];
        let cur: string | null = startPossessId;
        for (let d = 0; d < 20; d++) {
            if (!cur) return null;
            if (creatureIds.has(cur)) return cur;
            const row = inventories.find((inv: unknown) => ftInventoryField(inv, 'inventory_id') === cur);
            if (!row) return null;
            cur = ftInventoryField(row, 'possess_id');
        }
        return null;
    }

    /** Retired 2D host lookup retained as a null compatibility seam for generic inventory drag code. */
    /**
     * Sidelog `#caster_id` for the active page (the user's "controlling caster").
     */
    static _activePageCasterId(): string {
        if (typeof document === 'undefined') return '';
        const raw = String(window.ftGetActiveCasterId?.() || '').trim();
        if (!raw) return '';
        if (typeof checkIfValidUUID === 'function' && !checkIfValidUUID(raw)) return '';
        return raw;
    }

    /**
     * Map preferred_speed_type UUID → movement mode.
     */
    static _movementModeFromPreferredSpeedItemId(prefRaw: string): string {
        return movementModeFromPreferredSpeedItemId(prefRaw, PREFERRED_SPEED_TO_MODE, DEFAULT_MOVEMENT_MODE);
    }

    /**
     * Best encounter_arr for possess walks + caster stats.
     */
    static _bestEncounterArrForDrag(): EncounterArr | null {
        let eb: EncounterArr | null = null;
        if (typeof _ftGetEncounterArrForLightSourcePanel === 'function') {
            const candidate = _ftGetEncounterArrForLightSourcePanel();
            eb = recordOf(candidate) as EncounterArr | null;
        }
        return eb;
    }

    static _resolveInventoryRootCreatureContext(draggableEl: HTMLElement): { creatureId: string | null } {
        const empty = { creatureId: null };
        if (!draggableEl?.classList?.contains('inventory-item')) return empty;

        const possessId = draggableEl.dataset.possess_id || draggableEl.dataset.move_from_inventory_id || '';
        if (!possessId || typeof checkIfValidUUID !== 'function' || !checkIfValidUUID(possessId)) {
            return empty;
        }

        const tokenSelectors = `.encounter-creature[data-draggable_id="${possessId}"], .gm-encounter-creature-card[data-draggable_id="${possessId}"]`;
        let creatureId: string | null = document.querySelector(tokenSelectors) ? possessId : null;

        const encounterArrRich = DragManager._bestEncounterArrForDrag();
        if (!creatureId && encounterArrRich) {
            creatureId = DragManager._climbPossessToEncounterCreatureId(possessId, encounterArrRich);
        }

        if (!creatureId && typeof document !== 'undefined') {
            const pageCasterId = window.ftGetActiveCasterId?.() || '';
            if (
                pageCasterId
                && possessId.toLowerCase() === pageCasterId.toLowerCase()
                && checkIfValidUUID(pageCasterId)
            ) {
                creatureId = pageCasterId;
            }
        }
        if (!creatureId) {
            const equipWrap = draggableEl.closest('[data-dropzone_type="dropzone_equip_inventory"]');
            const dz = equipWrap?.getAttribute?.('data-dropzone_id') || '';
            if (dz && checkIfValidUUID(dz)) creatureId = dz;
        }
        if (!creatureId) {
            const anchor = draggableEl.closest('[id^="div_inventory_encounter_creature_"]');
            const raw = (anchor as HTMLElement | null)?.id?.startsWith?.('div_inventory_encounter_creature_')
                ? (anchor as HTMLElement).id.slice('div_inventory_encounter_creature_'.length)
                : '';
            if (raw && checkIfValidUUID(raw)) creatureId = raw;
        }

        if (!creatureId) return empty;
        return { creatureId };
    }

    static _creaturePreferredSpeedFromDomOrEncounter(creatureId: string): string {
        const t = document.querySelector(
            `.encounter-creature[data-draggable_id="${creatureId}"], .gm-encounter-creature-card[data-draggable_id="${creatureId}"]`,
        ) as HTMLElement | null;
        if (t?.dataset?.preferred_speed_type) return t.dataset.preferred_speed_type;
        const encounterArrRich = DragManager._bestEncounterArrForDrag();
        const crArr = encounterArrRich?.encounter_creatures_arr;
        if (!crArr) return '';
        const cr = crArr.find(
            (c: EncounterCreatureRow) => encounterCreatureField(c.encounter_creatures, 'encounter_creature_id') === creatureId,
        );
        return encounterCreatureField(cr?.encounter_creatures, 'preferred_speed_type') ?? '';
    }

    /**
     * Preferred speed item_id for the encounter creature that ultimately owns this inventory (on-hand or nested).
     */
    static _resolveInventoryPossessorPreferredSpeedType(draggableEl: HTMLElement): string {
        const { creatureId } = DragManager._resolveInventoryRootCreatureContext(draggableEl);
        if (!creatureId) return '';
        return DragManager._creaturePreferredSpeedFromDomOrEncounter(creatureId);
    }

    // ────────────────────── PUBLIC: start drag ──────────────────────
    start(event: DragInputEvent, draggableEl: HTMLElement): void | undefined {
        ftDebugLog('xz drag', { phase: 'DRAG START', el: draggableEl });
        if (this.state.isDragging) return this.cancel();
        if (!draggableEl?.classList.contains('draggable')) return;

        try {
            this._initDrag(event, draggableEl);
            this._setupGlobalListeners();

            ftDebugLog('template.js:start — drag start', () => ({
                inv: !!draggableEl.classList?.contains('inventory-item'),
                elClass: draggableEl.className,
            }));

            // Create the drag ghost (inventory, side panels, etc.)
            this._createGhostImage(draggableEl);

            // Light up the edge tabs so mobile/zoomed-desktop users see they can spring-load the panels.
            this._markMobileTabsDragTarget(true);
            this._armEncTorchInventoryAffordance(draggableEl);
            if (draggableEl.classList.contains('inventory-item') && typeof tradeDropzoneSetDragHint === 'function') {
                tradeDropzoneSetDragHint(draggableEl);
            }

            this._suspendLeafletMaps();
        } catch (err) {
            console.error('DragManager.start() failed:', err);
            this.cancel();
        }
    }

    // Pause drag/scroll handlers on every Leaflet map currently in the DOM.
    _suspendLeafletMaps(): void {
        if (typeof document === 'undefined' || typeof L === 'undefined') return;
        const maps = document.querySelectorAll('.leaflet-container');
        maps.forEach((el: Element) => {
            const host = el as LeafletHostElement;
            const m = host._ftLeafletMap;
            if (!m || host._ftLeafletMapDragSuspended) return;
            const prev = {
                dragging: !!(m.dragging && m.dragging.enabled && m.dragging.enabled()),
                scrollWheelZoom: !!(m.scrollWheelZoom && m.scrollWheelZoom.enabled && m.scrollWheelZoom.enabled()),
            };
            try { if (prev.dragging && m.dragging) m.dragging.disable(); } catch (e) { /* ignore */ }
            try { if (prev.scrollWheelZoom && m.scrollWheelZoom) m.scrollWheelZoom.disable(); } catch (e) { /* ignore */ }
            host._ftLeafletMapDragSuspended = prev;
        });
    }

    _resumeLeafletMaps(): void {
        if (typeof document === 'undefined') return;
        const maps = document.querySelectorAll('.leaflet-container');
        maps.forEach((el: Element) => {
            const host = el as LeafletHostElement;
            const m = host._ftLeafletMap;
            const prev = host._ftLeafletMapDragSuspended;
            if (!m || !prev) return;
            try { if (prev.dragging && m.dragging) m.dragging.enable(); } catch (e) { /* ignore */ }
            try { if (prev.scrollWheelZoom && m.scrollWheelZoom) m.scrollWheelZoom.enable(); } catch (e) { /* ignore */ }
            delete host._ftLeafletMapDragSuspended;
        });
    }

    // ────────────────────── PRIVATE: core init ──────────────────────
    _initDrag(event: DragInputEvent, draggableEl: HTMLElement) { this._tokenCtrl.initDrag(event as MouseEvent | TouchEvent, draggableEl); }

    // ────────────────────── PRIVATE: ghost image (non-XZ drags) ──────────────────────
    _createGhostImage(draggableEl: HTMLElement): void {
        const rect = draggableEl.getBoundingClientRect();

        const clone = draggableEl.cloneNode(true) as HTMLElement;

        // CRITICAL: Remove any outline/border that was copied from original
        clone.style.outline = 'none';
        clone.style.outlineOffset = '';
        clone.style.border = 'none';

        clone.style.opacity = '1';

        Object.assign(clone.style, {
            position: 'fixed',
            top: `${rect.top}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            height: `${rect.height}px`,
            margin: '0',
            padding: '0',
            pointerEvents: 'none',
            zIndex: '999999',
            boxShadow: '0 0 35px rgba(0, 255, 255, 0.8)',
            transform: 'scale(1.07)',
            transition: 'transform 0.15s ease-out, opacity 0.3s ease-out',
            background: 'transparent',
        });
        clone.classList.add('drag-ghost');

        document.body.appendChild(clone);
        this.state.dropPreview = clone;
    }

    /**
     * Mini preview inside the hovered encounter strip dropzone.
     */
    _syncGmDropzoneInlineGhost(dz: HTMLElement | null): void {
        document.querySelectorAll('.gm-dropzone-inline-ghost').forEach((n: Element) => n.remove());
        if (!dz || !this.state.draggedElement) {
            return;
        }
        const src = this.state.draggedElement as HTMLElement;
        if (
            !src.classList.contains('gm-encounter-creature-card')
            && !src.classList.contains('encounter-creature')
        ) {
            return;
        }
        const mini = src.cloneNode(true) as HTMLElement;
        mini.classList.add('gm-dropzone-inline-ghost');
        mini.classList.remove('drag-initialized', 'dragging');
        mini.removeAttribute('id');
        Object.assign(mini.style, {
            position: 'absolute',
            right: '6px',
            bottom: '4px',
            pointerEvents: 'none',
            opacity: '0.92',
            zIndex: '25',
            maxWidth: '76px',
            boxShadow: '0 0 14px rgba(13, 202, 240, 0.85)',
        });
        const pos = window.getComputedStyle(dz).position;
        if (pos === 'static' || pos === '') {
            dz.style.position = 'relative';
        }
        dz.appendChild(mini);
    }

    /**
     * Best-slot equip ghost (owner follow-up B): while an inventory-item drags anywhere over an
     * equipment paper-doll (`dropzone_equip_inventory`), ghost the item's art into the slot the
     * server WOULD equip it to — the client mirror of resolve_best_equipment_slot()
     * (move_inventory_inc.php): the item's `equipment_slot` layer, empty → off hand; non-hand
     * slots exact (first FREE cell for ring/waist multis); main/off weapons fall back to the free
     * other hand (a both-hands weapon counts as occupying both). No exact-slot hover needed.
     * Cells are located via the data-ft_slot / data-ft_slot_n stamps buildEquipmentColumn emits
     * (pc-sheet tabs/inventory.ts) — covers BOTH the in-sheet Inventory tab and the fullscreen
     * Equipment window. Idempotent per frame (keyed skip); removed in _cleanupDOM.
     */
    _syncInventoryEquipSlotGhost(dz: HTMLElement | null): void {
        const src = this.state.draggedElement as HTMLElement | null;
        const active = !!(
            dz
            && dz.getAttribute('data-dropzone_type') === 'dropzone_equip_inventory'
            && src?.classList?.contains('inventory-item')
            && !dz.contains(src) // re-arranging an already-equipped tile: no preview
        );
        const key = active
            ? `${dz!.getAttribute('data-dropzone_id') || ''}|${src!.dataset.inventory_id || src!.dataset.draggable_id || ''}`
            : '';
        if (key !== '' && key === this._equipGhostKey && document.querySelector('.ft-equip-slot-ghost')) return;
        this._equipGhostKey = key;
        document.querySelectorAll('.ft-equip-slot-ghost').forEach((n: Element) => n.remove());
        if (!active) return;

        // EquipmentSlot::LIST uuids (module-local copy — same pattern as inventory.ts / equipment-grid.ts).
        const MAIN = 'CEF334BF-DE96-1BEA-F72A-A4C98FBF36AD';
        const OFF  = '4FF85144-67EC-9D87-AE28-15137A1A2481';
        const BOTH = 'E6D02ACB-D3E9-DD8B-762A-31FE8C8D898D';

        const slotUuid = (v: unknown): string => {
            if (v == null) return '';
            if (typeof v === 'string') { const s = v.trim(); return checkIfValidUUID(s) ? s.toUpperCase() : ''; }
            if (Array.isArray(v)) return slotUuid(v[0]);
            const record = recordOf(v);
            if (record) {
                const items = recordOf(record.items);
                return slotUuid(firstValue(items?.item_id) ?? firstValue(record.item_id));
            }
            return '';
        };
        const eq = (a: string, b: string) => a !== '' && a.toUpperCase() === b.toUpperCase();

        // The paper-doll's blob is the occupancy source; the dragged item's DEF (for its preferred
        // equipment_slot layer) resolves via its instance in that blob, then the blob catalog, then
        // the global full-nest cache (item dragged from the map / another creature).
        const invId = String(src!.dataset.inventory_id || src!.dataset.draggable_id || '');
        const tplId = String(src!.dataset.inventory_item_id || '');
        const dzId = String(dz!.getAttribute('data-dropzone_id') || '');
        const cache = window.FateTide?.casterCache;
        const blob = recordOf(cache?.get(dzId)
            || cache?.get(window.ftGetActiveCasterId?.() || ''));
        const inv: unknown[] = Array.isArray(blob?.inventory) ? blob.inventory : [];

        const inst = inv.find((e: unknown) => eq(String(ftInventoryField(e, 'inventory_id') ?? ''), invId)) ?? null;
        let preferred = inst ? slotUuid(ftLayerValue(inst, 'equipment_slot')) : '';
        if (preferred === '' && tplId !== '') {
            const cat = recordOf(blob?.catalog);
            const def = cat ? (cat[tplId] || cat[tplId.toUpperCase()] || cat[tplId.toLowerCase()]) : null;
            if (def) preferred = slotUuid(ftLayerValue(def, 'equipment_slot'));
            if (preferred === '' && typeof window.ftFindFullNestedItem === 'function') {
                const full = window.ftFindFullNestedItem(tplId);
                if (full) preferred = slotUuid(ftLayerValue(full, 'equipment_slot'));
            }
        }
        if (preferred === '') preferred = OFF; // resolve_best_equipment_slot(): slotless → off hand

        // Occupancy per slot from the blob's equipped instances (the dragged item itself excluded so
        // re-equipping doesn't count as its own blocker).
        const perSlot = new Map<string, number>();
        for (const e of inv) {
            if (eq(String(ftInventoryField(e, 'inventory_id') ?? ''), invId)) continue;
            const s = slotUuid(readInventoryEquippedToken(e as Record<string, unknown>, blob ?? undefined));
            if (s === '') continue;
            perSlot.set(s, (perSlot.get(s) || 0) + 1);
        }
        const bothCnt = perSlot.get(BOTH) || 0;
        const occMain = (perSlot.get(MAIN) || 0) + bothCnt;
        const occOff = (perSlot.get(OFF) || 0) + bothCnt;

        // resolve_best_equipment_slot(): hand items fall back to the free OTHER hand; all else exact.
        let target = preferred;
        if (preferred === MAIN || preferred === OFF) {
            const prefOcc = preferred === MAIN ? occMain : occOff;
            const otherOcc = preferred === MAIN ? occOff : occMain;
            if (prefOcc >= 1 && otherOcc < 1) target = preferred === MAIN ? OFF : MAIN;
        }

        // Ring/waist multi-cells: preview in the first FREE cell (all full → cell 1, the swap target).
        const cellFor = (slotId: string): HTMLElement | null => {
            const all = Array.from(dz!.querySelectorAll(`.equipment-slot[data-ft_slot="${slotId}"]`)) as HTMLElement[];
            if (all.length === 0) return null;
            if (all.length === 1) return all[0] ?? null;
            const used = perSlot.get(slotId) || 0;
            const n = used < all.length ? used + 1 : 1;
            return all.find((c) => c.dataset.ft_slot_n === String(n)) ?? all[0] ?? null;
        };
        // A both-hands item previews in BOTH hand cells (faded right copy, like the real render).
        const targets: { el: HTMLElement | null; faded: boolean }[] = target === BOTH
            ? [{ el: cellFor(MAIN), faded: false }, { el: cellFor(OFF), faded: true }]
            : [{ el: cellFor(target), faded: false }];

        const media = src!.querySelector('img, video');
        for (const { el, faded } of targets) {
            if (!el) continue;
            const ghost = document.createElement('div');
            ghost.className = 'ft-equip-slot-ghost' + (faded ? ' ft-equip-slot-ghost-faded' : '');
            if (media) ghost.appendChild(media.cloneNode(true));
            const pos = window.getComputedStyle(el).position;
            if (pos === 'static' || pos === '') el.style.position = 'relative';
            el.appendChild(ghost);
        }
    }



    // ────────────────────── GLOBAL LISTENERS ──────────────────────
    _setupGlobalListeners(): void {
        const { handleMove, handleEnd } = this._bound;

        document.addEventListener('mousemove', handleMove);
        document.addEventListener('mouseup', handleEnd);

        document.addEventListener('touchmove', handleMove, { passive: false });
        document.addEventListener('touchend', handleEnd);
    }

    handleMove(e: DragInputEvent): void {
        if (!this.state.isDragging || !this.state.dropPreview) return;

        e.preventDefault();
        e.stopPropagation();

        // === GET CURSOR POSITION (mouse or touch) ===
        const clientX = e.clientX ?? e.touches?.[0]?.clientX;
        const clientY = e.clientY ?? e.touches?.[0]?.clientY;
        if (clientX == null || clientY == null) return;

        // Save for reliable drop on touchend
        this.state.lastValidPosition = { clientX, clientY };

        // List / panel ghost: follow cursor here; highlight dropzone + show mini preview in strip
        if (this.state.dropPreview?.classList?.contains('drag-ghost')) {
            const preview = this.state.dropPreview as HTMLElement;
            const pw = preview.offsetWidth || 1;
            const ph = preview.offsetHeight || 1;
            preview.style.left = `${clientX - pw / 2}px`;
            preview.style.top = `${clientY - ph / 2}px`;

            const hoverTarget = document.elementFromPoint(clientX, clientY);
            const dz = hoverTarget?.closest?.('.dropzone[data-dropzone_type]') as HTMLElement | null;
            document.querySelectorAll('.dropzone.gm-dropzone-hover').forEach((el) => {
                if (el !== dz) el.classList.remove('gm-dropzone-hover');
            });
            if (dz) {
                dz.classList.add('gm-dropzone-hover');
                this._syncGmDropzoneInlineGhost(dz);
            } else {
                this._syncGmDropzoneInlineGhost(null);
            }
            this._syncInventoryEquipSlotGhost(dz);
        }

        // === GROUND-ONLY STATE ===
        const groundOnly = !e.shiftKey
            && (this.state.movementMode === 'ground' || this.state.movementMode === 'hover');

        // === FALLBACK: Universal auto-scroll for non-XZ containers ===
        const under = document.elementFromPoint(clientX, clientY);
        if (under) {
            const vertScrollable = this.findNearestScrollable(under, 'vertical');
            if (vertScrollable) {
                this.autoScrollContainer(vertScrollable, clientX, clientY, 'vertical', groundOnly);
            }

            const horizScrollable = this.findNearestScrollable(under, 'horizontal');
            if (horizScrollable && horizScrollable !== vertScrollable) {
                this.autoScrollContainer(horizScrollable, clientX, clientY, 'horizontal', groundOnly);
            }
        }

        // === SPRING-LOADED MOBILE PANEL TABS ===
        this._updateMobileTabDwell(clientX, clientY);

        // === MOBILE: inventory drag over encounter torch → orange hint + dwell scroll to map ===
        this._updateEncTorchInventoryScrollHint(clientX, clientY);
    }

    /**
     * Spring-loaded behavior for .ft-mobile-panel-tab edge tabs.
     */
    _updateMobileTabDwell(clientX: number, clientY: number): void {
        if (typeof isNarrowViewport === 'function' && !isNarrowViewport()) {
            this._clearMobileTabDwell();
            return;
        }
        const under = document.elementFromPoint(clientX, clientY);
        const tab = under && under.closest
            ? (under.closest('.ft-mobile-panel-tab:not(.ft-mobile-panel-tab--enc)') as HTMLElement | null)
            : null;

        if (!tab) {
            this._clearMobileTabDwell();
            return;
        }

        const current = this.state.tabDwell;
        if (current && current.tab === tab) return;

        this._clearMobileTabDwell();

        const which = tab.id === 'ft_mobile_panel_tab_info' ? 'info' : 'log';
        const panelOpen = which === 'info' ? isSideInfoPanelOpen() : isSideLogOpen();

        tab.classList.add('is-dwelling');
        if (panelOpen) tab.classList.add('is-close-mode');

        const timeoutId = setTimeout(() => {
            if (which === 'info') {
                if (isSideInfoPanelOpen()) {
                    closeSideInfoPanel();
                } else {
                    forceOpenSideInfoPanel(true);
                }
            } else {
                if (isSideLogOpen()) {
                    closeSideLog();
                } else {
                    forceOpenSideLog();
                }
            }
            tab.classList.remove('is-dwelling');
            tab.classList.remove('is-close-mode');
            this.state.tabDwell = null;
        }, 400);

        this.state.tabDwell = { tab, which, timeoutId };
    }

    _clearMobileTabDwell(): void {
        const d = this.state && this.state.tabDwell;
        if (!d) return;
        clearTimeout(d.timeoutId);
        if (d.tab) {
            d.tab.classList.remove('is-dwelling');
            d.tab.classList.remove('is-close-mode');
        }
        this.state.tabDwell = null;
    }

    /**
     * Narrow viewports: as soon as an on-hand inventory drag starts, show orange on the encounter torch.
     */
    _armEncTorchInventoryAffordance(draggableEl: HTMLElement): void {
        if (typeof isNarrowViewport === 'function' && !isNarrowViewport()) return;
        if (!draggableEl?.classList.contains('inventory-item')) return;
        const encTab = document.getElementById('ft_mobile_panel_tab_enc');
        if (!encTab || encTab.classList.contains('d-none') || encTab.hasAttribute('hidden')) return;
        if (encTab.classList.contains('is-at-target')) return;
        encTab.classList.add('ft-mobile-panel-tab--enc-scroll-hint');
    }

    /**
     * Mobile <xl: while dragging on-hand inventory, keep the encounter torch orange.
     */
    _updateEncTorchInventoryScrollHint(clientX: number, clientY: number): void {
        if (typeof isNarrowViewport === 'function' && !isNarrowViewport()) {
            this._clearEncTorchInventoryScrollHint();
            return;
        }
        if (!this.state.isDragging) {
            this._clearEncTorchInventoryScrollHint();
            return;
        }
        const inv = this.state.draggedElement;
        if (!inv || !inv.classList.contains('inventory-item')) {
            this._clearEncTorchInventoryScrollHint();
            return;
        }

        const encTab = document.getElementById('ft_mobile_panel_tab_enc');
        if (!encTab || encTab.classList.contains('d-none') || encTab.hasAttribute('hidden')) {
            this._clearEncTorchInventoryScrollHint();
            return;
        }

        if (encTab.classList.contains('is-at-target')) {
            encTab.classList.remove('ft-mobile-panel-tab--enc-scroll-hint');
            const d = this.state.encTorchDwell;
            if (d?.timeoutId) clearTimeout(d.timeoutId);
            this.state.encTorchDwell = null;
            return;
        }

        encTab.classList.add('ft-mobile-panel-tab--enc-scroll-hint');

        const under = document.elementFromPoint(clientX, clientY);
        const overEnc = under && under.closest && under.closest('#ft_mobile_panel_tab_enc');
        if (!overEnc) {
            const d = this.state.encTorchDwell;
            if (d?.timeoutId) clearTimeout(d.timeoutId);
            this.state.encTorchDwell = null;
            return;
        }

        const current = this.state.encTorchDwell;
        if (current && current.tab === encTab) return;

        if (current?.timeoutId) clearTimeout(current.timeoutId);
        this.state.encTorchDwell = null;

        const timeoutId = setTimeout(() => {
            if (typeof ftScrollEncounterMapIntoViewForDrag === 'function') {
                ftScrollEncounterMapIntoViewForDrag();
            }
            this.state.encTorchDwell = { tab: encTab, timeoutId: null };
        }, 400);

        this.state.encTorchDwell = { tab: encTab, timeoutId };
    }

    _clearEncTorchInventoryScrollHint(): void {
        const d = this.state && this.state.encTorchDwell;
        if (d?.timeoutId) clearTimeout(d.timeoutId);
        this.state.encTorchDwell = null;
        const encTab = document.getElementById('ft_mobile_panel_tab_enc');
        if (encTab) {
            encTab.classList.remove('ft-mobile-panel-tab--enc-scroll-hint');
        }
    }

    /** Toggle the is-drag-target affordance on both mobile panel tabs. */
    _markMobileTabsDragTarget(on: boolean): void {
        document.querySelectorAll<HTMLElement>('.ft-mobile-panel-tab').forEach((t) => {
            if (t.id === 'ft_mobile_panel_tab_enc') return;
            t.classList.toggle('is-drag-target', !!on);
            if (!on) {
                t.classList.remove('is-dwelling');
                t.classList.remove('is-close-mode');
            }
        });
    }

    // Find nearest ancestor that can scroll in the given direction
    findNearestScrollable(element: Element, direction: 'vertical' | 'horizontal' = 'vertical'): HTMLElement | null {
        let el: Element | null = element;
        while (el && el !== document.body) {
            const style = window.getComputedStyle(el as HTMLElement);
            const overflow = direction === 'vertical' ? style.overflowY : style.overflowX;
            if (overflow === 'auto' || overflow === 'scroll') {
                const hasScroll = direction === 'vertical'
                    ? el.scrollHeight > el.clientHeight
                    : el.scrollWidth > el.clientWidth;
                if (hasScroll) return el as HTMLElement;
            }
            el = el.parentElement;
        }
        return null;
    }

    // Perform proportional auto-scroll on a container
    autoScrollContainer(container: HTMLElement, clientX: number, clientY: number, direction: 'vertical' | 'horizontal' = 'vertical', groundOnly: boolean = false): void {
        const rect = container.getBoundingClientRect();
        const threshold = 100;
        const maxSpeed = 50;

        let speed = 0;
        if (direction === 'vertical') {
            if (clientY < rect.top + threshold) {
                const dist = rect.top + threshold - clientY;
                speed = -(dist / threshold) * maxSpeed;
            } else if (clientY > rect.bottom - threshold) {
                const dist = clientY - (rect.bottom - threshold);
                speed = (dist / threshold) * maxSpeed;
            }

            if (speed !== 0) {
                container.scrollTop += speed;
            }
        } else {
            if (clientX < rect.left + threshold) {
                const dist = rect.left + threshold - clientX;
                speed = -(dist / threshold) * maxSpeed;
            } else if (clientX > rect.right - threshold) {
                const dist = clientX - (rect.right - threshold);
                speed = (dist / threshold) * maxSpeed;
            }

            if (speed !== 0) container.scrollLeft += speed;
        }
    }

    async handleEnd(e: DragInputEvent): Promise<void> {
        ftDebugLog('xz drag', {
            phase: 'handleEnd',
        });

        this._cleanupListeners();

        // Start the commit before cleanup: its synchronous prefix reads the drag state and resolves the drop target under the ghost.
        // Then end the gesture at once — never hold the drag lock across the commit's round-trip. While isDragging stayed true for
        // the whole move (fetch, compile, side-panel refresh), refreshDragAndDrop ignored every tile press, so the browser selected the
        // images instead (owner 2026-10-19), and a commit that rejected kept it that way until reload. The moved row's own in-flight
        // guard is ftSetInventoryMovePending.
        const source = this.state.draggedElement as HTMLElement | null;
        const commit = this.state.isDragging ? this._commitMove(e) : null;

        this._cleanupDOM();

        this.cancel();

        if (!commit) return;
        // The dropped tile itself stays reserved until its commit settles (the pending mark lands only after the move's first await).
        if (source) source.dataset.ftDragCommit = '1';
        try {
            await commit;
        } catch (err) {
            console.error('DragManager commit failed:', err);
        } finally {
            if (source) delete source.dataset.ftDragCommit;
        }
    }

    handleDragOver(e: Event): void {
        e.preventDefault();
    }

    // ────────────────────── COMMIT MOVE ──────────────────────
    async _commitMove(e: DragInputEvent): Promise<void> {
        ftDebugLog('xz drag', {
            phase: '_commitMove',
        });

        const { draggedElement: el, draggedItemId: id } = this.state;
        if (!el || !id) { this.cancel(); return; }

        const getPointerPos = () => {
            if (e.clientX !== undefined && e.clientY !== undefined) return { x: e.clientX, y: e.clientY };
            if (e.changedTouches?.[0]) return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
            const last = this.state.lastValidPosition;
            return { x: last.clientX, y: last.clientY };
        };

        const { x: clientX, y: clientY } = getPointerPos();

        // ==== 0. Creature released over the action-bar dock (Feature B) ====
        // The action-bar creature-target bridge consumes this drop (it slots the creature as a saved target
        // for an ability), so this is NOT a map move — decline the commit. Same elementFromPoint drop-target
        // pattern as the merchant / pickpocket branches below; cleanup + cancel run in handleEnd.
        if (
            el.classList.contains('encounter-creature')
            && document.elementFromPoint(clientX, clientY)?.closest('.ft3d-actionbar-dock')
        ) {
            return;
        }

        // ==== 1b. Merchant trade offer zones ====
        if (el.classList.contains('inventory-item') && window.tradeState && window.tradeState.active) {
            const underTradeEl = document.elementFromPoint(clientX, clientY);
            const tradeZone = underTradeEl?.closest(
                '[data-dropzone_type="dropzone_trade_player_offer"], [data-dropzone_type="dropzone_trade_merchant_offer"]'
            ) as HTMLElement | null;
            if (tradeZone) {
                tradeCommitDropFromDrag(el, tradeZone);
                return;
            }
        }

        // ==== 1c. Trainer payment offer zone ====
        if (el.classList.contains('inventory-item') && window.trainState && window.trainState.active) {
            const underTrainEl = document.elementFromPoint(clientX, clientY);
            const trainZone = underTrainEl?.closest('[data-dropzone_type="dropzone_train_player_offer"]') as HTMLElement | null;
            if (trainZone) {
                trainCommitDropFromDrag(el, trainZone);
                return;
            }
        }

        // (Sections 2 / 2c / 3 — the retired XZ widget's map drop — were removed 2026-09-27, Janus L2 S4 follow-up: the widget was
        // deleted 2026-08-31 and no drop could reach them. FT3D owns world movement; floor objects move through the I1 lane.)

        // ==== 2b. Pickpocket staging zone drop ====
        if (el.classList.contains('inventory-item')) {
            const underEl = document.elementFromPoint(clientX, clientY);
            const stagingZone = underEl?.closest('#pickpocket_staging_zone');
            if (stagingZone) {
                pickpocketStageItem(el);
                return;
            }
        }

        // ==== 4. Legacy external dropzones ====
        const under = document.elementFromPoint(clientX, clientY);
        const target = under?.closest('[data-dropzone_type]') as HTMLElement | null;

        if (target) {
            const dropzoneType = target.dataset.dropzone_type;
            const dropzoneId = target.dataset.dropzone_id;

            const isGmListCreatureCard = el.classList.contains('gm-encounter-creature-card');
            const isMarkerToken = el.classList.contains('ft-marker-token');
            if (dropzoneType === 'dropzone_encounter' && (el.classList.contains('encounter-creature') || isGmListCreatureCard || isMarkerToken)) {
                await encounter_creatures_moved(id, dropzoneId as string);
                return;
            }
            if (dropzoneType === 'dropzone_encounter_link' && (el.classList.contains('encounter-creature') || isGmListCreatureCard || isMarkerToken)) {
                await encounter_linked_creatures_moved(id, dropzoneId as string);
                return;
            }

            const validInventoryTypes = [
                'dropzone_move_inventory',
                'dropzone_equip_inventory',
                'dropzone_give_inventory',
                'dropzone_drop_inventory',
            ];

            if (el.classList.contains('inventory-item') && validInventoryTypes.includes(dropzoneType as string)) {
                // A container panel can contain both its own draggable tile and nested container
                // dropzones. Never send a request that would reparent the container to itself or
                // below itself. The server independently walks the possess_id chain as the
                // authoritative guard, because not every descendant is necessarily mounted in DOM.
                if (dropzoneId === id || el.contains(target)) {
                    displayWarning('A container cannot be moved into itself or one of its contents.');
                    return;
                }

                // Keyring dropzones (data-dropzone_accept="key", stamped by the PC-sheet renderer)
                // take ONLY keys — instant client reject, no server call. The server's
                // validate_move_into_keyring_keys_only() remains the authoritative gate.
                if (
                    String(target.dataset.dropzone_accept || '') === 'key'
                    && String(el.dataset.item_type || '') !== 'key'
                ) {
                    displayWarning('A keyring can only hold keys.');
                    return;
                }

                const moveType = (dropzoneType as string).replace(/^dropzone_/, '');
                const qty = parseInt(el.dataset.inventory_qty || '') || 1;
                const fromId = el.dataset.move_from_inventory_id || null;
                const showLog = parseInt(el.dataset.show_log_message || '1');
                const grossWeight = tokenGrossWeight(el);
                let dropCap = tokenCapacityRemaining(target) ?? Infinity;
                if (tokenIsMerchant(target)) {
                    dropCap = Infinity;
                }

                if (
                    qty === 1
                    && Number.isFinite(dropCap)
                    && grossWeight > dropCap + 1e-6
                ) {
                    displayWarning('Cannot move that much weight there (carrying capacity exceeded).');
                    return;
                }

                if (qty > 1) {
                    showInventorySliderPopup(id, qty, fromId, dropzoneId, moveType, showLog, true);
                } else {
                    await move_draggable_inventory(id, 1, fromId, dropzoneId, moveType, showLog);
                }

                return;
            }
        }

        // ==== 5. No valid target ====
        ftDebugLog('xz drag', { cancelled: 'no valid drop target' });
        this.cancel();
    }


    // ────────────────────── CLEANUP — MUST MATCH THE ADD ──────────────────────
    _cleanupListeners(): void {
        const { handleMove, handleEnd } = this._bound;

        document.removeEventListener('mousemove', handleMove);
        document.removeEventListener('mouseup', handleEnd);
        document.removeEventListener('touchmove', handleMove);
        document.removeEventListener('touchend', handleEnd);

        this.state._dragOverCleanup?.();
        delete this._lastLog;
    }

    _cleanupDOM(): void {
        if (typeof tradeDropzoneClearDragHint === 'function') {
            tradeDropzoneClearDragHint();
        }
        document.querySelectorAll('.dropzone.gm-dropzone-hover').forEach((el) => el.classList.remove('gm-dropzone-hover'));
        document.querySelectorAll('.gm-dropzone-inline-ghost').forEach((n: Element) => n.remove());
        document.querySelectorAll('.ft-equip-slot-ghost').forEach((n: Element) => n.remove());
        this._equipGhostKey = '';

        // Remove the drag ghost.
        this.state.dropPreview?.remove();

        // Restore original thumbnail
        const el = this.state.draggedElement as HTMLElement | null;
        if (el) {
            el.classList.remove('dragging', 'being-dragged', 'ft3d-cancel-hover');
            // A GM's move marks the row pending synchronously inside _commitMove; keep that dim until the move settles.
            if (el.dataset.ftMovePending !== '1') el.style.opacity = '';
            el.style.transition = '';
            el.style.outline = '';
            el.style.outlineOffset = '';
        }

        // Clear spring-loaded mobile panel tab state (timers + visual affordances)
        this._clearMobileTabDwell();
        this._clearEncTorchInventoryScrollHint();
        this._markMobileTabsDragTarget(false);

        // Pair with _initDrag's addition — hide-dead-in-combat resumes.
        document.body.classList.remove('ft3d-drag-active');
    }

    // ────────────────────── PUBLIC: cancel ──────────────────────
    cancel(): void {
        // Run even if isDragging was cleared earlier (e.g. _commitMove already called cancel)
        // Wipe OA threat + overrun decorations on every cancel path.
        window.FT3D_OA_THREAT?.clear();
        window.FT3D_OVERRUN?.clear();

        if (!this.state.isDragging) {
            // Defensive — if start() suspended Leaflet drag and we bailed
            // before isDragging was set, still resume.
            this._resumeLeafletMaps();
            return;
        }

        this._cleanupListeners();
        this._cleanupDOM();

        // Re-enable Leaflet panning/zoom we paused at start().
        this._resumeLeafletMaps();

        const el = this.state.draggedElement as HTMLElement | null;
        if (el) {
            el.classList.remove('dragging');
            if (el.dataset.ftMovePending !== '1') el.style.opacity = '';
            el.style.transition = '';
            el.style.outline = '';
            el.style.outlineOffset = '';

            if (el.dataset.origX !== undefined) el.dataset.x = el.dataset.origX;
            if (el.dataset.origZ !== undefined) el.dataset.z = el.dataset.origZ;
            delete el.dataset.origX;
            delete el.dataset.origZ;
        }

        this.state = {
            isDragging: false,
            draggedElement: null,
            draggedItemId: null,
            dropPreview: null,
            lastValidPosition: { clientX: 0, clientY: 0 },
            movementMode: 'ground',
            _dragOverCleanup: null,

            tabDwell: null,

            encTorchDwell: null,
        };
    }


    distance(p1: FT3DPoint, p2: FT3DPoint): number { return geomDistance(p1, p2); }



    // ────────────────────── SVG HELPERS ──────────────────────
    static createSVG(className: string, zIndex: number | string = 1000): SVGSVGElement {
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.classList.add(className);
        Object.assign(svg.style, {
            position: 'absolute', top: 0, left: 0,
            width: '100%', height: '100%',
            pointerEvents: 'none', zIndex,
        });
        return svg;
    }






    animateInventoryPickup(inventoryEl: HTMLElement, newOwnerId: string): void {
        if (!inventoryEl || !newOwnerId) return;

        const targetCreatureEl = document.getElementById(`creature_${newOwnerId}`)
                              || document.querySelector(`[data-draggable_id="${newOwnerId}"]`);

        if (!targetCreatureEl) {
            // Fallback: simple fade out
            this._fadeOutAndRemove(inventoryEl);
            return;
        }

        this._flyToCreatureAndVanish(inventoryEl, targetCreatureEl as HTMLElement);
    }

    _flyToCreatureAndVanish(movingEl: HTMLElement, targetCreatureEl: HTMLElement): void {
        const fromRect = movingEl.getBoundingClientRect();
        const toRect   = targetCreatureEl.getBoundingClientRect();

        const clone = movingEl.cloneNode(true) as HTMLElement;
        clone.style.position = 'fixed';
        clone.style.left     = fromRect.left + 'px';
        clone.style.top      = fromRect.top + 'px';
        clone.style.width    = fromRect.width + 'px';
        clone.style.height   = fromRect.height + 'px';
        clone.style.margin   = '0';
        clone.style.zIndex   = '9999';
        clone.style.pointerEvents = 'none';
        clone.style.transition = 'none';
        clone.style.filter = 'drop-shadow(0 0 12px gold)';
        clone.style.transform = 'translateZ(0)'; // Force GPU
        document.body.appendChild(clone);

        // Force reflow
        void clone.offsetHeight;

        // Animate!
        requestAnimationFrame(() => {
            clone.style.transition = 'all 0.65s cubic-bezier(0.2, 0.8, 0.4, 1)';
            clone.style.left = (toRect.left + toRect.width / 2 - fromRect.width / 2) + 'px';
            clone.style.top  = (toRect.top + toRect.height / 2 - fromRect.height / 2) + 'px';
            clone.style.transform = 'scale(0.15) rotate(380deg)';
            clone.style.opacity = '0';
        });

        // Cleanup
        const cleanup = () => {
            if (clone && clone.parentNode) clone.parentNode.removeChild(clone);
            if (movingEl && movingEl.parentNode) movingEl.parentNode.removeChild(movingEl);
            refreshDragAndDrop();
        };

        clone.addEventListener('transitionend', cleanup);
        setTimeout(cleanup, 800); // Safety
    }

    _fadeOutAndRemove(el: HTMLElement): void {
        el.style.transition = 'opacity 0.4s ease-out, transform 0.4s ease-out';
        el.style.opacity = '0';
        el.style.transform = 'scale(0.4)';
        setTimeout(() => {
            if (el && el.parentNode) el.parentNode.removeChild(el);
            refreshDragAndDrop();
        }, 450);
    }

    // ────────────────────── PUBLIC: Safe Remove with Optional Animation ──────────────────────
    removeInventorySafely(inventoryEl: HTMLElement, options: { animate?: boolean; flyToCreatureId?: string | null; fadeOnly?: boolean } = {}): void {
        if (!inventoryEl || !inventoryEl.parentNode) return;

        const {
            animate = true,
            flyToCreatureId = null,
            fadeOnly = false,
        } = options;

        // PHASE 1: INSTANT DEATH
        inventoryEl.style.pointerEvents = 'none';
        inventoryEl.classList.add('dying');
        inventoryEl.setAttribute('data-dead', 'true');
        inventoryEl.removeAttribute('draggable');
        inventoryEl.classList.remove('draggable');

        // Refresh draggable bindings after the optimistic removal. `updateTargeting` was a stale
        // optional call: no current drag owner defines it.
        refreshDragAndDrop?.();

        // PHASE 2: Animation (non-blocking)
        if (!animate) {
            inventoryEl.remove();
            return;
        }

        if (flyToCreatureId) {
            this.animateInventoryPickup(inventoryEl, flyToCreatureId);
            return;
        }

        if (fadeOnly || !animate) {
            this._fadeOutAndRemove(inventoryEl);
            return;
        }

        // Default: beautiful poof vanish
        inventoryEl.style.transition = 'all 0.6s cubic-bezier(0.16, 1, 0.3, 1)';
        inventoryEl.style.opacity = '0';
        inventoryEl.style.transform = 'scale(0.3) translateY(-80px)';

        const cleanup = () => {
            if (inventoryEl && inventoryEl.parentNode) {
                inventoryEl.parentNode.removeChild(inventoryEl);
            }
            refreshDragAndDrop();
        };

        inventoryEl.addEventListener('transitionend', cleanup, { once: true });
        setTimeout(cleanup, 700);
    }
}


/**
 * Rebind newly rendered inventory/encounter elements to the singleton drag owner.
 * This helper used to live in the retired XZ bootstrap; DragManager is its real dependency.
 */
function refreshDragAndDrop(): void {
    document.querySelectorAll<HTMLElement>('.draggable:not(.drag-initialized)').forEach((el) => {
        el.classList.add('drag-initialized');
        const startDrag = (event: MouseEvent | TouchEvent): void => {
            if (el.classList.contains('persistent-aoe-item')) return;
            if ('button' in event && event.button && event.button !== 0) return;
            // Claim the press first: a tile press must never fall through to native image selection, even while it cannot drag.
            event.preventDefault();
            if (dragManager.state.isDragging || el.dataset.ftDragCommit === '1') return;
            let moved = false;
            const threshold = 6;
            const startX = 'touches' in event ? event.touches[0]?.clientX : event.clientX;
            const startY = 'touches' in event ? event.touches[0]?.clientY : event.clientY;
            if (startX === undefined || startY === undefined) return;
            const cleanup = (): void => {
                document.removeEventListener('mousemove', moveHandler);
                document.removeEventListener('touchmove', moveHandler);
                document.removeEventListener('mouseup', upHandler);
                document.removeEventListener('touchend', upHandler);
            };
            const moveHandler = (moveEvent: MouseEvent | TouchEvent): void => {
                const moveX = 'touches' in moveEvent ? moveEvent.touches[0]?.clientX : moveEvent.clientX;
                const moveY = 'touches' in moveEvent ? moveEvent.touches[0]?.clientY : moveEvent.clientY;
                if (moveX === undefined || moveY === undefined) return;
                if (!moved && (Math.abs(moveX - startX) > threshold || Math.abs(moveY - startY) > threshold)) {
                    moved = true;
                    dragManager.start(moveEvent, el);
                    cleanup();
                }
            };
            const upHandler = (): void => cleanup();
            document.addEventListener('mousemove', moveHandler);
            document.addEventListener('touchmove', moveHandler, { passive: false });
            document.addEventListener('mouseup', upHandler);
            document.addEventListener('touchend', upHandler);
        };
        el.addEventListener('mousedown', startDrag);
        el.addEventListener('touchstart', startDrag, { passive: false });
        el.addEventListener('dragstart', event => event.preventDefault());
    });
    document.querySelectorAll<HTMLElement>('.dropzone:not(.dropzone-feedback)').forEach((dropzone) => {
        dropzone.classList.add('dropzone-feedback');
        dropzone.addEventListener('pointerenter', () => {
            if (dragManager.state.isDragging) dropzone.classList.add('dragging-over');
        });
        dropzone.addEventListener('pointerleave', () => dropzone.classList.remove('dragging-over'));
        dropzone.addEventListener('pointerup', () => dropzone.classList.remove('dragging-over'));
    });
    document.querySelectorAll<HTMLImageElement>('img').forEach((image) => {
        if (image.getAttribute('draggable') !== 'false') image.setAttribute('draggable', 'false');
        if (image.style.pointerEvents !== 'none') image.style.pointerEvents = 'none';
    });
}

// ────────────────────── SINGLETON ──────────────────────
const dragManager = new DragManager();
Object.assign(window, { DragManager, dragManager, refreshDragAndDrop });
